<script setup>
import { computed, useAttrs, nextTick } from 'vue';
import { normalizeUrl } from '@/utils/helpers';

const props = defineProps({
  to: { type: [String, Object], default: undefined },
  href: { type: String, default: undefined },
  target: { type: String, default: undefined },
  type: { type: String, default: 'button' },
  isStatic: { type: Boolean, default: false },
  tag: { type: String, default: 'div' },
});

const attrs = useAttrs();
const emit = defineEmits(['click']);

const isExternalTo = computed(() => {
  const url = props.to || props.href;
  if (typeof url !== 'string') return false;

  const isAbsolute = /^(https?:)?\/\//.test(url);

  if (isAbsolute && process.client) {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.hostname !== window.location.hostname;
    } catch (e) {
      return true;
    }
  }

  return isAbsolute;
});

const component = computed(() => {
  if (props.isStatic) return props.tag;

  const url = props.to || props.href;
  if (!url) return 'button';

  if (!isExternalTo.value) {
    return defineNuxtLink({});
  }

  return 'a';
});

const componentProps = computed(() => {
  const _props = { ...attrs };
  const url = props.to || props.href;

  if (
    component.value?.name === 'NuxtLink' ||
    typeof component.value === 'function'
  ) {
    _props.to = url;
  } else if (component.value === 'a') {
    _props.href = normalizeUrl(url);

    const target = props.target ?? (isExternalTo.value ? '_blank' : undefined);
    if (target) {
      _props.target = target;
      if (target === '_blank') _props.rel = 'noopener noreferrer';
    }
  } else if (component.value === 'button') {
    _props.type = props.type;
  }

  return _props;
});

const scrollToBlockNoHash = async (hashUrl) => {
  if (process.server) return false;

  const url = typeof hashUrl === 'string' ? hashUrl.trim() : '';
  if (!url.startsWith('#') || url.length < 2) return false;

  const id = decodeURIComponent(url.slice(1));

  await nextTick();

  const el =
    document.getElementById(id) ||
    document.querySelector(`[id="${CSS.escape(id)}"]`);

  if (!el) return false;

  const headerOffset = 88; // Висота фіксованого хедеру
  const top =
    el.getBoundingClientRect().top + window.pageYOffset - headerOffset;

  window.scrollTo({ top, behavior: 'smooth' });
  return true;
};

const handleClick = async (event) => {
  const url = props.href || props.to;

  // Якщо це якірне посилання (починається з #)
  if (typeof url === 'string' && url.startsWith('#')) {
    event.preventDefault();
    await scrollToBlockNoHash(url);
  }

  emit('click', event);
};
</script>

<template>
  <component :is="component" v-bind="componentProps" @click="handleClick">
    <slot />
  </component>
</template>
